import { isSSR } from 'utils/isSSR';

interface CacheEntry<T> {
	data: T;
	expireAt: number;
	frequency: number;
}

const cache = new Map<string, CacheEntry<any>>();
const CACHE_TTL = 1000 * 60 * 15;
const MAX_CACHE_SIZE = Number(globalThis?.process?.env?.CONF_MAX_CACHE_SIZE || 5000);
let lastCleanup = Date.now();
const CLEANUP_INTERVAL = CACHE_TTL / 3;

export function setCache(key: string, data: any, cacheTtl = CACHE_TTL) {
	const now = Date.now();
	const expireAt = now + cacheTtl;

	// Lazy cleanup
	if (now - lastCleanup > CLEANUP_INTERVAL) {
		cleanupCache(now);
	}

	if (cache.size >= MAX_CACHE_SIZE && !cache.has(key)) {
		// First try to clean expired items
		if (cleanupCache(now) === 0) {
			// If cleaning didn't help, remove least frequently used items
			evictLeastFrequentlyUsed();
		}
	}

	// Preserve frequency if the item already exists
	const existingEntry = cache.get(key);
	const frequency = existingEntry ? existingEntry.frequency : 0;

	// Update or create the item
	cache.set(key, { data, expireAt, frequency });
}

export function getCache<T>(key: string): T | undefined {
	const cached = cache.get(key);

	if (cached) {
		const now = Date.now();
		if (now < cached.expireAt) {
			cached.frequency += 1;
			return cached.data;
		}

		cache.delete(key);
	}

	return undefined;
}

function cleanupCache(now: number): number {
	let removedCount = 0;

	for (const [key, value] of cache.entries()) {
		if (now >= value.expireAt) {
			cache.delete(key);
			removedCount++;
		}
	}

	if (removedCount > 0) {
		lastCleanup = now;
	}

	return removedCount;
}

function evictLeastFrequentlyUsed(): void {
	// Safer way to work with cache items
	const keysWithFrequencies: [string, number][] = [];

	for (const [key, entry] of cache.entries()) {
		keysWithFrequencies.push([key, entry.frequency]);
	}

	// Sort by frequency (from lowest to highest)
	keysWithFrequencies.sort((a, b) => a[1] - b[1]);

	// Remove 10% of least frequently used items or at least one item
	const itemsToRemove = Math.max(1, Math.floor(cache.size * 0.1));
	for (let i = 0; i < itemsToRemove && i < keysWithFrequencies.length; i++) {
		cache.delete(keysWithFrequencies[i]![0]);
	}
}

if (isSSR()) {
	setInterval(() => {
		const now = Date.now();
		cleanupCache(now);
		lastCleanup = now;
	}, CLEANUP_INTERVAL);
}
